/**
 * A field edited where it is read. Click (or Enter on it) to edit; Enter or Tab saves, Esc puts
 * it back. The API is the validator, so its message shows under the field. If someone else saved
 * the record first, the field says what they changed it to and asks: keep mine, or use theirs.
 * A field the rules own is plain text with the reason on hover, and no edit affordance at all.
 *
 * Fetches nothing: the page hands in `save` and `refresh`.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { ApiError } from "../api/client";

export type InlineKind = "text" | "number" | "date" | "select" | "check";

export function InlineField({
  label,
  value,
  display,
  kind = "text",
  options,
  empty,
  mono,
  locked,
  full,
  save,
  refresh,
}: {
  label: string;
  /** The value as it is typed: MM-DD-YYYY for a date, a plain number, an option's key. */
  value: string;
  /** How it reads when not editing; defaults to the value or the option's label. */
  display?: ReactNode;
  kind?: InlineKind;
  options?: readonly (readonly [string, string])[];
  /** The "nothing chosen" option's label, for a select that may be empty. */
  empty?: string;
  mono?: boolean;
  /** Why this field cannot be edited here; set means read-only. */
  locked?: string;
  full?: boolean;
  save: (value: string) => Promise<void>;
  /** After a stale save: the record's value now, as typed. */
  refresh: () => Promise<string>;
}) {
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [theirs, setTheirs] = useState<string | null>(null);
  const [found, setFound] = useState(value);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement & HTMLSelectElement>(null);

  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  const labelOf = (v: string) =>
    options?.find(([k]) => k === v)?.[1] ?? (v === "" ? (empty ?? "") : v);
  const shown = display ?? (kind === "select" ? labelOf(value) : value);
  const isEmpty = shown === null || shown === undefined || shown === "";

  const commit = async (next: string) => {
    if (next === value && theirs === null) {
      setEditing(false);
      setError(null);
      return;
    }
    setBusy(true);
    try {
      await save(next);
      setEditing(false);
      setError(null);
      setTheirs(null);
    } catch (err) {
      if (err instanceof ApiError && err.code === "stale_version") {
        setTheirs(await refresh());
        setError(null);
      } else if (err instanceof ApiError) {
        const fieldMessage = Object.values(err.fields)[0];
        setError(fieldMessage ?? err.message);
      } else {
        setError("Couldn't save. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
    setError(null);
    setTheirs(null);
  };

  if (kind === "check") {
    return (
      <div className={`fv ifv${full ? " full" : ""}`}>
        <dt id={id}>{label}</dt>
        <dd>
          <label className="check">
            <input
              type="checkbox"
              aria-labelledby={id}
              checked={value === "true"}
              disabled={Boolean(locked) || busy}
              title={locked}
              onChange={(e) => void commit(String(e.target.checked))}
            />
            {value === "true" ? "Yes" : "No"}
          </label>
          {error && <div className="field-error">{error}</div>}
        </dd>
      </div>
    );
  }

  if (locked) {
    return (
      <div className={`fv${full ? " full" : ""}`}>
        <dt>{label}</dt>
        <dd className={isEmpty ? "empty-v" : mono ? "mono" : undefined} title={locked}>
          {isEmpty ? "–" : shown}
        </dd>
      </div>
    );
  }

  if (!editing) {
    return (
      <div className={`fv ifv${full ? " full" : ""}`}>
        <dt id={id}>{label}</dt>
        <dd>
          <button
            type="button"
            className={`ifv-read${isEmpty ? " empty-v" : ""}${mono ? " mono" : ""}`}
            aria-labelledby={id}
            aria-description="Edit"
            onClick={() => {
              setDraft(value);
              setFound(value);
              setEditing(true);
            }}
          >
            {isEmpty ? "–" : shown}
          </button>
        </dd>
      </div>
    );
  }

  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter") {
      e.preventDefault();
      void commit(draft);
    }
  };

  return (
    <div className={`fv ifv is-editing${full ? " full" : ""}`}>
      <dt>
        <label htmlFor={id}>{label}</label>
      </dt>
      <dd>
        {kind === "select" ? (
          <select
            id={id}
            ref={input}
            className={`field field-sm${error ? " is-invalid" : ""}`}
            value={draft}
            disabled={busy}
            onChange={(e) => {
              setDraft(e.target.value);
              void commit(e.target.value);
            }}
            onKeyDown={keys}
            onBlur={() => theirs === null && !busy && cancel()}
          >
            {empty !== undefined && <option value="">{empty}</option>}
            {options?.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={id}
            ref={input}
            className={`field field-sm${mono ? " mono" : ""}${error ? " is-invalid" : ""}`}
            value={draft}
            disabled={busy}
            inputMode={kind === "number" ? "decimal" : undefined}
            placeholder={kind === "date" ? "MM-DD-YYYY" : undefined}
            aria-invalid={Boolean(error)}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={keys}
            onBlur={() => theirs === null && !busy && void commit(draft)}
          />
        )}
        {error && <div className="field-error">{error}</div>}
        {theirs !== null && (
          <div className="ifv-merge" role="alert">
            {theirs === found ? (
              <span>Someone else saved this record first. This field is as you found it.</span>
            ) : (
              <span>
                Someone else changed this first, to <b>{labelOf(theirs) || "empty"}</b>
              </span>
            )}
            <button type="button" className="btn btn-sm" onClick={() => void commit(draft)}>
              {theirs === found ? "Save mine" : "Keep mine"}
            </button>
            <button type="button" className="btn btn-sm btn-quiet" onClick={cancel}>
              {theirs === found ? "Cancel" : "Use theirs"}
            </button>
          </div>
        )}
      </dd>
    </div>
  );
}
